import { forwardRef, useImperativeHandle, useRef } from "react";
import { TextInput } from "react-native";
import { FieldValues, useFormContext } from "react-hook-form";

import { VnBox } from "@/components/VnBox";
import {
  BaseFormInputPropsType,
  VnFormControl,
  VnFormErrorMessage,
  VnFormField,
  VnFormItem,
  VnFormLabel,
} from "@/components/VnForm";
import { BaseTextInput } from "@/components/VnTextInput/BaseTextInput";
import { useTranslator } from "@/hooks/useTranslate";
import { cn } from "@/utils/tailwind";

type DateOfBirthInputPropsType<T extends FieldValues> =
  BaseFormInputPropsType<T> & {
    onYearSubmit?: () => void;
  };

type DateOfBirthInputHandlesType = {
  focusMonth: () => void;
  focusYear: () => void;
};

const VnDateOfBirthInputComponent = <T extends FieldValues>(
  {
    name,
    control,
    isRequired = false,
    labelI18nKey,
    labelI18nOptions,
    showError = true,
    errorI18nKey,
    errorI18nOptions,
    className,
    onYearSubmit,
  }: DateOfBirthInputPropsType<T>,
  ref: React.Ref<DateOfBirthInputHandlesType>,
) => {
  const { t } = useTranslator();
  const { trigger } = useFormContext();

  const dayRef = useRef<TextInput>(null);
  const monthRef = useRef<TextInput>(null);
  const yearRef = useRef<TextInput>(null);

  useImperativeHandle(ref, () => ({
    focusMonth: () => monthRef.current?.focus(),
    focusYear: () => yearRef.current?.focus(),
  }));

  return (
    <VnFormField
      name={name}
      control={control}
      render={({ field }) => {
        const parseDateValue = (dateString: string | undefined) => {
          if (!dateString || typeof dateString !== "string") {
            return { day: "", month: "", year: "" };
          }

          // Handle ISO format (YYYY-MM-DD) from server/initial values
          if (dateString.includes("-") && !dateString.includes("|")) {
            const [year, month, day] = dateString.split("-");
            return {
              month: month ? String(parseInt(month, 10)) : "",
              day: day ? String(parseInt(day, 10)) : "",
              year: year ?? "",
            };
          }

          // Handle internal pipe format (M|D|YYYY)
          const parts = dateString.split("|");
          if (parts.length === 3) {
            return {
              month: parts[0] ?? "",
              day: parts[1] ?? "",
              year: parts[2] ?? "",
            };
          }
          return { day: "", month: "", year: "" };
        };

        const formatDateValue = ({
          day,
          month,
          year,
        }: {
          day: string;
          month: string;
          year: string;
        }) => {
          // If all fields are empty, return undefined for nullish validation
          if (!day && !month && !year) {
            return undefined;
          }

          // If all fields are filled, create ISO format for backend
          if (day && month && year.length === 4) {
            const paddedMonth = month.padStart(2, "0");
            const paddedDay = day.padStart(2, "0");
            return `${year}-${paddedMonth}-${paddedDay}`;
          }

          // Otherwise store raw values separated by pipe to preserve exact user input
          return `${month}|${day}|${year}`;
        };

        const currentValue = parseDateValue(field.value);

        const handleBlur = () => {
          field.onBlur();
          const isDateComplete =
            Boolean(currentValue.month) &&
            Boolean(currentValue.day) &&
            currentValue.year.length === 4;
          if (isDateComplete) {
            void trigger(name);
          }
        };

        const handleDayChange = (day: string) => {
          const formatted = formatDateValue({
            day,
            month: currentValue.month,
            year: currentValue.year,
          });
          field.onChange(formatted);
        };

        const handleMonthChange = (month: string) => {
          const formatted = formatDateValue({
            day: currentValue.day,
            month,
            year: currentValue.year,
          });
          field.onChange(formatted);
        };

        const handleYearChange = (year: string) => {
          const formatted = formatDateValue({
            day: currentValue.day,
            month: currentValue.month,
            year,
          });
          field.onChange(formatted);
        };

        return (
          <VnFormItem>
            <VnBox className={cn("gap-2", className)}>
              {labelI18nKey && (
                <VnFormLabel
                  i18nKey={labelI18nKey}
                  i18nOptions={labelI18nOptions}
                  isRequired={isRequired}
                />
              )}

              <VnFormControl>
                <VnBox className="flex flex-row gap-4">
                  <BaseTextInput
                    placeholder={t("native:dateOfBirthInput.monthPlaceholder")}
                    value={currentValue.month}
                    onChangeText={handleMonthChange}
                    onBlur={handleBlur}
                    keyboardType="number-pad"
                    textContentType="birthdateMonth"
                    autoComplete="birthdate-month"
                    ref={monthRef}
                    returnKeyType="next"
                    onSubmitEditing={() => dayRef.current?.focus()}
                    maxLength={2}
                  />
                  <BaseTextInput
                    placeholder={t("native:dateOfBirthInput.dayPlaceholder")}
                    value={currentValue.day}
                    onChangeText={handleDayChange}
                    onBlur={handleBlur}
                    keyboardType="number-pad"
                    textContentType="birthdateDay"
                    autoComplete="birthdate-day"
                    ref={dayRef}
                    returnKeyType="next"
                    onSubmitEditing={() => yearRef.current?.focus()}
                    maxLength={2}
                  />
                  <BaseTextInput
                    placeholder={t("native:dateOfBirthInput.yearPlaceholder")}
                    value={currentValue.year}
                    onChangeText={handleYearChange}
                    onBlur={handleBlur}
                    keyboardType="number-pad"
                    textContentType="birthdateYear"
                    autoComplete="birthdate-year"
                    ref={yearRef}
                    returnKeyType="next"
                    onSubmitEditing={onYearSubmit}
                    maxLength={4}
                  />
                </VnBox>
              </VnFormControl>
            </VnBox>
            {showError && (
              <VnFormErrorMessage
                i18nKey={errorI18nKey}
                i18nOptions={errorI18nOptions}
              />
            )}
          </VnFormItem>
        );
      }}
    />
  );
};

export const VnDateOfBirthInput = forwardRef(VnDateOfBirthInputComponent) as <
  T extends FieldValues,
>(
  props: DateOfBirthInputPropsType<T> & {
    ref?: React.Ref<DateOfBirthInputHandlesType>;
  },
) => ReturnType<typeof VnDateOfBirthInputComponent>;
